import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
  createContext
} from 'react';
import deepmerge from 'deepmerge';

import { Logger, setFromSession } from 'wtb-utils';
import { useBillingContext } from 'context';
import { ANALYTICS_EVENTS, PAYMENT_TYPES } from 'constants.js';
import { useSendAnalyticsEvent } from 'hooks';
import { cleanCustomerDataToBE } from 'utils';

const TransactionContext = createContext();

const defaultState = {
  actions: [],
  analyticsScript: null,
  areTermsAccepted: false,
  billing: {},
  campaign: {},
  customer: {},
  customerData: null,
  customerDataErrors: [],
  customerDataHasError: true,
  formRequiredFields: {},
  hasSentDetails: false,
  links: { merchant: {}, wtb: {} },
  merchant: {},
  order: {},
  payment: {},
  sign: {},
  paymentPlans: [],
  paymentType: null,
  selectedPaymentPlan: null,
  transaction: {},
  triedToPay: false,
  wtbTerms: null,
  emailValidationData: {}
};

const TransactionProvider = ({ children, initialState: state = {} }) => {
  const { hasBillingInfo } = useBillingContext();

  const initialState = deepmerge(defaultState, state);

  const sendAnalyticsEvent = useSendAnalyticsEvent();

  const [ actions, setActions ] = useState(initialState.actions);
  const [ analyticsScript, setAnalyticsScript ] = useState(initialState.analyticsScript);
  const [ areTermsAccepted, setAreTermsAccepted ] = useState(initialState.areTermsAccepted);
  const [ campaign, setCampaign ] = useState(initialState.campaign);
  const [ customer, setCustomer ] = useState(initialState.customer);
  const [ customerData, setCustomerData ] = useState(initialState.customerData);
  const [ customerDataErrors, setCustomerDataErrors ] = useState(initialState.customerDataErrors);
  const [ customerDataHasError, setCustomerDataHasError ] = useState(initialState.customerDataHasError);
  const [ formRequiredFields, setFormRequiredFields ] = useState(initialState.formRequiredFields);
  const [ hasSentDetails, setHasSentDetails ] = useState(initialState.hasSentDetails);
  const [ links, setLinks ] = useState(initialState.links);
  const [ merchant, setMerchant ] = useState(initialState.merchant);
  const [ order, setOrder ] = useState(initialState.order);
  const [ payment, setPayment ] = useState(initialState.payment);
  const [ sign, setSign ] = useState(initialState.sign);
  const [ paymentPlans, setPaymentPlans ] = useState(initialState.paymentPlans);
  const [ selectedPaymentPlan, setSelectedPaymentPlan ] = useState(initialState.selectedPaymentPlan);
  const [ transaction, setTransaction ] = useState(initialState.transaction);
  const [ triedToPay, setTriedToPay ] = useState(initialState.triedToPay);
  const [ wtbTerms, setWtbTerms ] = useState(initialState.wtbTerms);
  const [ emailValidationData, setEmailValidationData ] = useState(initialState.emailValidationData);

  const hasTerms = useCallback(
    () => !!(merchant.terms || wtbTerms),
    [ merchant.terms, wtbTerms ]
  );

  useEffect(() => {
    if (merchant?.name) {
      // console.log(merchant.name, sendAnalyticsEvent);
      sendAnalyticsEvent(ANALYTICS_EVENTS.MERCHANT, { name: merchant.name });
    }
  }, [ merchant?.name, sendAnalyticsEvent ]);

  // TODO: Add the campaign id on analytics after its added on BE
  // useEffect(() => {
  //   if (campaign?.id) {
  //     sendAnalyticsEvent(ANALYTICS_EVENTS.CAMPAIGN, { id: campaign.id });
  //   }
  // }, [ campaign?.id, sendAnalyticsEvent ]);

  useEffect(() => {
    if (selectedPaymentPlan?.type) {
      sendAnalyticsEvent(ANALYTICS_EVENTS.SELECTED_PAYMENT_PLAN, { plan: selectedPaymentPlan.type });
    }
  }, [ selectedPaymentPlan?.type, sendAnalyticsEvent ]);

  const canPay = useCallback(() => {
    if (!selectedPaymentPlan?.type) return false;

    const commonChecks = !customerDataHasError;

    switch (selectedPaymentPlan.type) {
      case PAYMENT_TYPES.INVOICE: {
        return commonChecks && hasBillingInfo();
      }
      case PAYMENT_TYPES.CARD:
      default: return commonChecks;
    }
  }, [ customerDataHasError, hasBillingInfo, selectedPaymentPlan ]);

  const getCleanCustomerData = useCallback(() => cleanCustomerDataToBE(customerData), [ customerData ]);

  useEffect(() => {
    if (!selectedPaymentPlan || !paymentPlans || !paymentPlans.length) return;

    const plan = paymentPlans.find((p) => p.id === selectedPaymentPlan.id);

    if (!plan) {
      Logger.error(`Plan with ID ${selectedPaymentPlan.id} not found.`);
    }
  }, [ paymentPlans, selectedPaymentPlan ]);

  useEffect(() => {
    if (!selectedPaymentPlan) return;

    setFromSession('paymentPlan', JSON.stringify(selectedPaymentPlan));
  }, [ selectedPaymentPlan ]);

  const hasAboutInfo = useCallback(
    () => formRequiredFields.name || formRequiredFields.nationalId,
    [ formRequiredFields.name, formRequiredFields.nationalId ]
  );
  const hasContactInfo = useCallback(
    () => formRequiredFields.email || formRequiredFields.phone,
    [ formRequiredFields.email, formRequiredFields.phone ]
  );
  const hasShippingInfo = useCallback(
    () => !!formRequiredFields.address,
    [ formRequiredFields.address ]
  );

  const hasPersonalInfo = useCallback(() => {
    if (formRequiredFields.name && (!customerData?.firstName || !customerData?.lastName)) return false;
    if (formRequiredFields.nationalId && !customerData?.nationalId) return false;

    if (formRequiredFields.email && !customerData?.email) return false;
    if (formRequiredFields.phone && !customerData?.phone) return false;

    if (formRequiredFields.address && (
      !customerData?.street
      || !customerData?.zipCode
      || !customerData?.city
      || !customerData?.country
    )) return false;

    return true;
  }, [ formRequiredFields, customerData ]);

  return (
    <TransactionContext.Provider
      value={{
        actions, setActions,
        analyticsScript, setAnalyticsScript,
        areTermsAccepted, setAreTermsAccepted,
        campaign, setCampaign,
        customer, setCustomer,
        customerData, setCustomerData, getCleanCustomerData,
        customerDataErrors, setCustomerDataErrors,
        customerDataHasError, setCustomerDataHasError,
        formRequiredFields, setFormRequiredFields,
        hasSentDetails, setHasSentDetails,
        links, setLinks,
        merchant, setMerchant,
        order, setOrder,
        payment, setPayment,
        sign, setSign,
        paymentPlans, setPaymentPlans,
        selectedPaymentPlan, setSelectedPaymentPlan,
        transaction, setTransaction,
        triedToPay, setTriedToPay,
        wtbTerms, setWtbTerms,
        emailValidationData, setEmailValidationData,
        canPay,
        hasTerms,
        hasAboutInfo,
        hasContactInfo,
        hasShippingInfo,
        hasPersonalInfo
      }}
    >
      {children}
    </TransactionContext.Provider>
  );
};

const TransactionConsumer = TransactionContext.Consumer;

const useTransactionContext = () => {
  const context = useContext(TransactionContext);

  if (context === undefined) {
    throw new Error('useTransactionContext must be used within a TransactionProvider');
  }

  return context;
};

export {
  TransactionContext, TransactionProvider, TransactionConsumer, useTransactionContext
};
